import React, { type ReactElement } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Loading from 'atom-components/loading';
import RoutePolicy, { type AuthorisableRoute } from 'policies/route.policy';
import { useAuth } from 'services/providers/auth.provider';
import { usePageAccess } from 'services/providers/page-access.provider';

type Props = {
  route: AuthorisableRoute;
  redirectPath?: string;
};

/**
 * Only allow access to the route if the user has the appropriate permissions.
 */
export default function RestrictedRoute({ route, redirectPath = '/' }: Props): ReactElement {
  const { authorisableUser, authorisableArea, isLoading } = usePageAccess();

  const location = useLocation();
  const { isAuthenticated } = useAuth();

  /* if loading don't render routes yet */
  if (isLoading) {
    return <Loading />;
  }

  const isAuthorised = RoutePolicy.view(authorisableUser, authorisableArea, route);

  /* if not authorised and auth state is determined redirect to base route */
  if (!isAuthenticated && !isAuthorised && !isLoading) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  if (isAuthorised) {
    /* render child routes */
    return <Outlet />;
  }

  return <Navigate to={redirectPath} state={{ from: location }} replace />;
}
